// 바인딩 select picker — 반환 구조 전체를 펼친 트리로 보여주고, 트리에서 쓸 값을
// 단일 선택한다. 선택 경로가 통과하는 배열은 노드 아래 줄에서 항목(인덱스)을 지정하고,
// 배열 노드 자체를 선택하면 '전체 나열'(repeat)이 된다.
// 조건(필터)은 배열 노드가 아니라 그 배열의 직속 leaf 필드 옆 인라인 '필터: = 값'으로
// 지정한다(부모 배열의 itemSpec을 condition으로 설정). 엔진 문법(getByPath
// `field[key=value]`)·value-set 라벨 드롭다운은 그대로 재사용한다.
// 결정 기록: docs/decisions/2026-06-29-composer-binding-section-v2.md

import { useId, useMemo, useState } from 'react';
import { VALUE_SETS, type CatalogEntry } from '@safetyhub/shared';
import {
  ROOT_KEY,
  buildCatalogTree,
  catalogSegmentLabels,
  mapArrayInfo,
  parseSelectPath,
  selectToTreeState,
  treeStateToSelect,
  treeStateToSelectWithItem,
  type ArrayItemSpec,
  type CatalogTreeNode,
  type MapArrayInfo,
  type TreeState,
} from './catalogPaths';
import { koreanFieldType } from './bindingSummary';

/**
 * 일괄 바인딩에서 선택 셀들의 select 경로가 제각각일 때, 경로를 select 세그먼트(레벨)
 * 단위로 비교해 "일치하는 레벨은 선택 유지, 다른 레벨만 '-'"로 표시하기 위한 정보.
 *
 * 각 셀의 select를 selectToTreeState로 변환한 뒤 fold한다.
 *  - `state`     = 공통 부분을 반영한 대표 TreeState(picker가 state로 사용 → 공통 레벨은
 *                  기존 checked 로직이 그대로 동작, 사용자가 한 레벨만 바꿔도 나머지는 유지).
 *  - `modeMixed` = 배열 노드 key 중 항목 모드(전체 나열 vs 특정 항목)가 셀마다 다른 것.
 *  - `indexMixed`= 특정 항목 인덱스가 셀마다 다른 배열 노드 key(인덱스 입력을 '-'로).
 *  - `condMixed` = 조건(필터) 값이 셀마다 다른 배열 노드 key.
 *  - `targetMixed` = 최종 선택 필드가 셀마다 다름(필드 라디오 미선택 + 혼합 라벨).
 */
export interface SelectMix {
  state: TreeState;
  modeMixed: Set<string>;
  indexMixed: Set<string>;
  condMixed: Set<string>;
  targetMixed: boolean;
  /** 어느 레벨이라도 혼합이면 true(안내 배너·readback용). */
  anyMixed: boolean;
}

/** targetKey(구조 키, dot-path)에서 ROOT부터의 조상 key 체인을 복원(항목지정 무관). */
function ancestorKeys(targetKey: string): string[] {
  if (targetKey === ROOT_KEY) return [ROOT_KEY];
  const keys: string[] = [ROOT_KEY];
  let acc = '';
  for (const p of targetKey.split('.')) {
    acc = acc ? `${acc}.${p}` : p;
    keys.push(acc);
  }
  return keys;
}

/** 한 셀 상태에서 배열 노드 key와의 관계: 특정 항목(item) / 전체 나열(list) / 경로 밖(off). */
function relationToArray(st: TreeState, key: string): 'item' | 'list' | 'off' {
  if (st.itemSpecs[key]) return 'item';
  if (st.targetKey === key) return 'list';
  return 'off';
}

function collectNodes(nodes: CatalogTreeNode[], map: Map<string, CatalogTreeNode>): void {
  for (const n of nodes) {
    map.set(n.key, n);
    collectNodes(n.children, map);
  }
}

/**
 * 선택 셀들의 select 문자열들을 select 세그먼트(레벨) 단위로 비교해 SelectMix를 산출한다.
 * 공통 조상 체인까지는 대표 state로 보존하고, 갈라지는 레벨만 *Mixed로 표시한다.
 * 인덱스+필드 배열 케이스(예 '0.field' vs '1.field')를 정확히 다루는 데 초점을 둔다.
 */
export function computeSelectMix(entry: CatalogEntry, selects: string[]): SelectMix {
  const empty: SelectMix = {
    state: { targetKey: ROOT_KEY, itemSpecs: {} },
    modeMixed: new Set(),
    indexMixed: new Set(),
    condMixed: new Set(),
    targetMixed: false,
    anyMixed: false,
  };
  if (selects.length === 0) return empty;

  const tree = buildCatalogTree(entry);
  const nodeMap = new Map<string, CatalogTreeNode>();
  collectNodes(tree, nodeMap);
  const states = selects.map((s) => selectToTreeState(entry, s));

  // 공통 조상 체인(노드 key 기준) — 대표 target은 마지막 공통 노드.
  const chains = states.map((st) => ancestorKeys(st.targetKey));
  let commonLen = Math.min(...chains.map((c) => c.length));
  for (let i = 0; i < commonLen; i++) {
    const k = chains[0][i];
    if (!chains.every((c) => c[i] === k)) {
      commonLen = i;
      break;
    }
  }
  const commonChain = chains[0].slice(0, Math.max(commonLen, 1)); // 최소 ROOT 보장
  const repTargetKey = commonChain[commonChain.length - 1];
  const targetMixed = !states.every((st) => st.targetKey === states[0].targetKey);

  const itemSpecs: Record<string, ArrayItemSpec> = {};
  const modeMixed = new Set<string>();
  const indexMixed = new Set<string>();
  const condMixed = new Set<string>();

  for (const key of commonChain) {
    const node = nodeMap.get(key);
    if (!node?.isArray) continue;
    const rels = states.map((st) => relationToArray(st, key));
    const allItem = rels.every((r) => r === 'item');
    const allList = rels.every((r) => r === 'list');
    if (!allItem && !allList) {
      modeMixed.add(key); // 전체 나열 vs 특정 항목 등 모드 혼합 → 둘 다 미선택
      continue;
    }
    if (allList) continue; // 전체 나열(target 배열) — itemSpec 없음
    // allItem — 항목 spec fold(인덱스/조건)
    const specs = states.map((st) => st.itemSpecs[key]).filter(Boolean) as ArrayItemSpec[];
    const allIndex = specs.every((s) => s.mode === 'index');
    const allCond = specs.every((s) => s.mode === 'condition');
    if (allIndex) {
      const base = specs[0];
      const baseIndex = base.mode === 'index' ? base.index : 0;
      itemSpecs[key] = { mode: 'index', index: baseIndex };
      if (!specs.every((s) => s.mode === 'index' && s.index === baseIndex)) indexMixed.add(key);
    } else if (allCond) {
      const base = specs[0];
      if (base.mode === 'condition') {
        itemSpecs[key] = { mode: 'condition', condKey: base.condKey, condVal: base.condVal };
        const same = specs.every(
          (s) => s.mode === 'condition' && s.condKey === base.condKey && s.condVal === base.condVal,
        );
        if (!same) condMixed.add(key);
      } else {
        itemSpecs[key] = { mode: 'index', index: 0 };
        condMixed.add(key);
      }
    } else {
      // 인덱스/조건 혼재 — 인덱스 기본 + 혼합 표시
      itemSpecs[key] = { mode: 'index', index: 0 };
      indexMixed.add(key);
    }
  }

  const anyMixed =
    targetMixed || modeMixed.size > 0 || indexMixed.size > 0 || condMixed.size > 0;
  return {
    state: { targetKey: repTargetKey, itemSpecs },
    modeMixed,
    indexMixed,
    condMixed,
    targetMixed,
    anyMixed,
  };
}

function arrayItemSpecEq(a: ArrayItemSpec | undefined, b: ArrayItemSpec | undefined): boolean {
  if (!a || !b) return a === b;
  if (a.mode !== b.mode) return false;
  if (a.mode === 'index' && b.mode === 'index') return a.index === b.index;
  if (a.mode === 'condition' && b.mode === 'condition')
    return a.condKey === b.condKey && a.condVal === b.condVal;
  return false;
}

/**
 * 일괄(개별) 바인딩 적용 시 셀별 select를 병합한다.
 * - 혼합(mixed)이던 세그먼트 중 사용자가 대표값 그대로 둔 것 → 각 셀의 원래 값 보존.
 * - 사용자가 대표값과 다르게 바꾼 세그먼트 + 타깃(필드) + 비혼합 세그먼트 → draft 값으로 통일.
 *
 * @param entry       카탈로그 엔트리
 * @param draftSelect 사용자가 picker에서 만든 통일 select(혼합 세그먼트는 대표 baseIndex 보유)
 * @param cellSelect  이 셀의 원래 select
 * @param mix         computeSelectMix(entry, 원래 셀들 select) 결과
 */
export function mergeBulkSelect(
  entry: CatalogEntry,
  draftSelect: string | undefined,
  cellSelect: string | undefined,
  mix: SelectMix,
): string | undefined {
  const draftState = selectToTreeState(entry, draftSelect);
  const cellState = selectToTreeState(entry, cellSelect);
  const mergedItemSpecs: Record<string, ArrayItemSpec> = { ...draftState.itemSpecs };
  const mixedKeys = new Set<string>([...mix.indexMixed, ...mix.modeMixed, ...mix.condMixed]);
  for (const key of mixedKeys) {
    const draftSpec = draftState.itemSpecs[key];
    const repSpec = mix.state.itemSpecs[key];
    // 사용자가 이 혼합 세그먼트를 명시적으로 바꿨으면(draft≠대표) draft 값으로 통일(그대로 둠).
    if (!arrayItemSpecEq(draftSpec, repSpec)) continue;
    // 미변경 혼합 세그먼트 → 셀 원래 값 보존(셀이 list 모드였으면 itemSpec 제거).
    const cellSpec = cellState.itemSpecs[key];
    if (cellSpec) mergedItemSpecs[key] = cellSpec;
    else delete mergedItemSpecs[key];
  }
  // 타깃(필드)은 항상 사용자 선택(draft) 사용.
  return treeStateToSelectWithItem(entry, draftState.targetKey, mergedItemSpecs);
}

interface Props {
  entry: CatalogEntry | undefined;
  value: string | undefined;
  onChange: (select: string | undefined) => void;
  disabled?: boolean;
  /**
   * 일괄 바인딩에서 선택 셀들의 select 경로가 제각각(혼합)일 때 전달.
   * value(=commonSelect)는 undefined이지만 이는 "전체 나열"이 아니라 "혼합"이므로,
   * selectMix.state(대표 상태)를 picker state로 사용하고, 일치하는 레벨은 선택을 유지하며
   * 갈라지는 레벨만 '-'/"— 혼합 —"로 나타낸다. 사용자가 한 레벨을 고치면 그 값이 전체 셀에
   * 통일된다(나머지 공통 레벨은 대표 상태로 보존).
   */
  selectMix?: SelectMix;
}

const FIELD_CLS =
  'text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400';

const DEFAULT_SPEC: ArrayItemSpec = { mode: 'index', index: 0 };

export function CatalogPathPicker({
  entry,
  value,
  onChange,
  disabled = false,
  selectMix,
}: Props) {
  const radioName = useId();
  const [showPath, setShowPath] = useState(false);

  const tree = useMemo(() => (entry ? buildCatalogTree(entry) : []), [entry]);
  // controlled — 혼합(selectMix)이면 공통 부분을 반영한 대표 상태를 쓰고(레벨별 선택 유지),
  // 단일/공통이면 prop value를 트리 상태로 파싱. 편집 시 treeStateToSelect로 onChange.
  const state = useMemo<TreeState>(
    () =>
      selectMix
        ? selectMix.state
        : entry
          ? selectToTreeState(entry, value)
          : { targetKey: ROOT_KEY, itemSpecs: {} },
    [entry, value, selectMix],
  );

  // 풀이용: 현재 select의 한글 라벨 체인.
  const readbackLabels = useMemo(
    () => (entry ? catalogSegmentLabels(entry, parseSelectPath(value)) : []),
    [entry, value],
  );

  if (!entry) {
    return (
      <div className={`${FIELD_CLS} w-full bg-gray-100 text-gray-400`}>
        데이터 출처를 먼저 선택하세요
      </div>
    );
  }

  const emit = (next: TreeState) => {
    onChange(treeStateToSelect(entry, next.targetKey, next.itemSpecs));
  };

  // 배열이 target이어도 itemSpec(특정 항목 인덱스)을 보존하는 emit — '특정 항목' 모드에서
  // 필드 미선택 상태(배열 자체가 target)도 select='<index>'로 안정 유지.
  const emitKeepItem = (next: TreeState) => {
    onChange(treeStateToSelectWithItem(entry, next.targetKey, next.itemSpecs));
  };

  const selectTarget = (key: string) => emit({ targetKey: key, itemSpecs: state.itemSpecs });

  const setItemSpec = (key: string, spec: ArrayItemSpec) =>
    emit({ targetKey: state.targetKey, itemSpecs: { ...state.itemSpecs, [key]: spec } });

  // 배열 항목 컨트롤용(인덱스 변경) — 배열 자체가 target일 때도 select 유지.
  const setItemSpecKeepTarget = (key: string, spec: ArrayItemSpec) =>
    emitKeepItem({ targetKey: state.targetKey, itemSpecs: { ...state.itemSpecs, [key]: spec } });

  // '특정 항목' 모드 진입 — 그 배열을 target으로 + index 0 지정(select='<index>').
  const pickArrayItem = (key: string) =>
    emitKeepItem({ targetKey: key, itemSpecs: { ...state.itemSpecs, [key]: { mode: 'index', index: 0 } } });

  // '전체 나열' 모드 진입 — 그 배열의 itemSpec 제거 + target으로(select=undefined).
  const listArray = (key: string) => {
    const rest: Record<string, ArrayItemSpec> = Object.fromEntries(
      Object.entries(state.itemSpecs).filter(([k]) => k !== key),
    );
    emit({ targetKey: key, itemSpecs: rest });
  };

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      {/* 헤더: 데이터 출처 + 필드명 표시 토글 */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium text-gray-600">{entry.label}</span>
        <label className="inline-flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer">
          <input
            type="checkbox"
            checked={showPath}
            onChange={(e) => setShowPath(e.target.checked)}
          />
          필드명 표시
        </label>
      </div>

      {/* 혼합 안내 — 일부 레벨만 셀마다 다르다. 일치하는 레벨은 선택이 유지된다. */}
      {selectMix?.anyMixed && (
        <div className="mb-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] text-amber-700">
          일부 경로가 셀마다 다릅니다. 일치하는 단계는 유지되고, 다른 단계만 ‘-’로 표시됩니다.
          값을 고치면 전체 셀에 통일됩니다.
        </div>
      )}

      {/* 펼친 트리 */}
      <div className="border border-gray-200 rounded max-h-64 overflow-y-auto overflow-x-hidden py-1">
        {tree.map((node) => (
          <TreeRow
            key={node.key}
            node={node}
            depth={0}
            state={state}
            selectMix={selectMix}
            radioName={radioName}
            showPath={showPath}
            onSelectTarget={selectTarget}
            onSetItemSpec={setItemSpec}
            onSetItemSpecKeepTarget={setItemSpecKeepTarget}
            onPickArrayItem={pickArrayItem}
            onListArray={listArray}
            onEmit={emit}
          />
        ))}
      </div>

      {/* readback: select 경로 + 한글 풀이 */}
      <div className="mt-1 text-[10px] text-gray-500 space-y-0.5">
        <div>
          <span className="text-gray-400">선택: </span>
          <span className="font-mono text-gray-600">
            {selectMix?.anyMixed ? '(혼합)' : value || '(전체)'}
          </span>
        </div>
        {!selectMix?.anyMixed && readbackLabels.length > 0 && (
          <div>
            <span className="text-gray-400">풀이: </span>
            {readbackLabels.join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}

interface TreeRowProps {
  node: CatalogTreeNode;
  depth: number;
  state: TreeState;
  /** 일괄 혼합 정보(없으면 단일 셀). 레벨별로 일치 레벨은 선택 유지, 갈라진 레벨만 혼합 표시. */
  selectMix?: SelectMix;
  radioName: string;
  showPath: boolean;
  /** 직속 부모가 배열이면 그 배열 key(=leaf 인라인 필터 대상), 아니면 undefined. */
  parentArrayKey?: string;
  onSelectTarget: (key: string) => void;
  onSetItemSpec: (key: string, spec: ArrayItemSpec) => void;
  /** 배열 인덱스 변경(배열 자체가 target일 때도 select 유지). */
  onSetItemSpecKeepTarget: (key: string, spec: ArrayItemSpec) => void;
  /** 배열 '특정 항목' 모드 진입(target=배열, index 0). */
  onPickArrayItem: (key: string) => void;
  /** 배열 '전체 나열' 모드 진입(itemSpec 제거, target=배열). */
  onListArray: (key: string) => void;
  /** target + itemSpecs를 한 번에 갱신(맵 뷰: 키 선택=조건+값 필드 동시 설정). */
  onEmit: (next: TreeState) => void;
}

function TreeRow({
  node,
  depth,
  state,
  selectMix,
  radioName,
  showPath,
  parentArrayKey,
  onSelectTarget,
  onSetItemSpec,
  onSetItemSpecKeepTarget,
  onPickArrayItem,
  onListArray,
  onEmit,
}: TreeRowProps) {
  // 대표 state 기반 — 공통 레벨의 선택은 그대로 표시. 혼합 레벨만 아래에서 마커로 덮는다.
  const isTarget = state.targetKey === node.key;
  // 이 배열 노드의 레벨별 혼합 여부.
  const modeMixed = selectMix?.modeMixed.has(node.key) ?? false;
  const indexMixed = selectMix?.indexMixed.has(node.key) ?? false;
  const condMixed = selectMix?.condMixed.has(node.key) ?? false;
  // 이 배열이 target(특정 항목)인데 하위 필드가 셀마다 다름 → 필드 혼합 라벨.
  const fieldMixed =
    !!selectMix?.targetMixed && isTarget && node.isArray && !modeMixed && !!state.itemSpecs[node.key];
  const indent = { paddingLeft: `${depth * 14 + 4}px` };
  // 항목 컨트롤 줄은 노드보다 라디오 폭(~18px)만큼 더 들여써 label 아래로 정렬.
  const subIndent = { paddingLeft: `${depth * 14 + 4 + 18}px` };
  // 루트 객체(single)는 선택지 없이 컨테이너로만 — 자식만 렌더.
  const isContainerRoot = node.key === ROOT_KEY && node.isObject;
  // 키-값 맵 배열이면 하위 필드를 접고 키 선택→값 출력 맵 뷰로 렌더.
  const mapInfo = node.isArray && !isContainerRoot ? mapArrayInfo(node) : null;

  if (mapInfo) {
    // 맵 뷰: 노드 행(라벨만) + 아래 맵 컨트롤(특정 항목/전체 나열). 자식 렌더 생략.
    // 맵/조건은 무리하게 레벨 분해하지 않고 이 노드의 혼합 여부 전체로 "— 혼합 —" 처리한다.
    const mapMixed =
      modeMixed || condMixed || (!!selectMix?.targetMixed && state.targetKey === node.key);
    const valueFieldKeys = mapInfo.valueFields.map((v) => v.key);
    const mapSelected = !mapMixed && (isTarget || valueFieldKeys.includes(state.targetKey));
    return (
      <div>
        <div
          className="flex flex-wrap items-center gap-1.5 px-1 py-0.5 text-xs"
          style={indent}
        >
          <span
            className={`min-w-0 break-words ${
              mapSelected ? 'font-semibold text-brand-700' : 'text-gray-700'
            }`}
          >
            {node.label}
          </span>
          <span className="shrink-0 text-[9px] text-gray-400 border border-gray-200 rounded px-1">
            {koreanFieldType(node.type)}
          </span>
          {showPath && node.segName && (
            <span className="shrink-0 text-[10px] text-gray-400 font-mono break-all">
              {node.key}
            </span>
          )}
        </div>
        <div className="px-1 pb-0.5" style={subIndent}>
          <MapArrayControls
            node={node}
            info={mapInfo}
            state={state}
            mixed={mapMixed}
            radioName={radioName}
            onEmit={onEmit}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {!isContainerRoot && (
        <>
          <div
            className="flex flex-wrap items-center gap-1.5 px-1 py-0.5 text-xs"
            style={indent}
          >
            {/* leaf만 단일 선택 대상(레이블 클릭). 배열은 아래 모드 토글로 선택. */}
            {node.isLeaf ? (
              <label className="inline-flex items-center gap-1.5 min-w-0 cursor-pointer">
                <input
                  type="radio"
                  name={radioName}
                  checked={isTarget}
                  onChange={() => onSelectTarget(node.key)}
                  className="shrink-0"
                />
                <span
                  className={`min-w-0 break-words ${
                    isTarget ? 'font-semibold text-brand-700' : 'text-gray-700'
                  }`}
                >
                  {node.label}
                </span>
              </label>
            ) : (
              <span
                className={`min-w-0 break-words ${
                  isTarget ? 'font-semibold text-brand-700' : 'text-gray-700'
                }`}
              >
                {node.label}
              </span>
            )}
            <span className="shrink-0 text-[9px] text-gray-400 border border-gray-200 rounded px-1">
              {koreanFieldType(node.type)}
            </span>
            {showPath && node.segName && (
              <span className="shrink-0 text-[10px] text-gray-400 font-mono break-all">
                {node.key}
              </span>
            )}
            {/* 배열 직속 leaf 필드 옆 인라인 필터(부모 배열 itemSpec=condition).
                조건 필터는 enum 키 배열에서만 의미 — enum 필드(valueSetId 보유)만 노출. */}
            {node.isLeaf && parentArrayKey && node.valueSetId && (
              <LeafFilter
                arrayKey={parentArrayKey}
                leaf={node}
                spec={state.itemSpecs[parentArrayKey]}
                onSetItemSpec={onSetItemSpec}
              />
            )}
          </div>
          {/* 배열 노드 모드 토글 — 노드 아래 별도 줄(수평 스크롤 방지). itemSpec 유무로
              모드 판정, 상시 표시: '전체 나열'(repeat) / '특정 항목'(인덱스 입력).
              '특정 항목'이면 ArrayItemControls(인덱스) 노출, 하위 필드 선택 시
              select='<index>.<field>'로 직렬화(엔진 기존 동작). */}
          {node.isArray && (
            <div className="px-1 pb-0.5 flex flex-col gap-0.5" style={subIndent}>
              <ArrayModeToggle
                node={node}
                isTarget={isTarget}
                hasItemSpec={!!state.itemSpecs[node.key]}
                modeMixed={modeMixed}
                modeName={`${radioName}__mode__${node.key}`}
                onListArray={onListArray}
                onPickArrayItem={onPickArrayItem}
              />
              {!modeMixed && !!state.itemSpecs[node.key] && (
                <ArrayItemControls
                  node={node}
                  spec={state.itemSpecs[node.key] ?? DEFAULT_SPEC}
                  indexMixed={indexMixed}
                  condMixed={condMixed}
                  onSetItemSpec={onSetItemSpecKeepTarget}
                />
              )}
              {fieldMixed && (
                <span className="text-[10px] text-gray-400">필드 — 혼합 —</span>
              )}
            </div>
          )}
        </>
      )}
      {node.children.map((child) => (
        <TreeRow
          key={child.key}
          node={child}
          depth={isContainerRoot ? depth : depth + 1}
          state={state}
          selectMix={selectMix}
          radioName={radioName}
          showPath={showPath}
          // 배열 노드는 자식에게 자기 key 전달(→직속 leaf=필터 대상), 비배열은 undefined.
          parentArrayKey={node.isArray ? node.key : undefined}
          onSelectTarget={onSelectTarget}
          onSetItemSpec={onSetItemSpec}
          onSetItemSpecKeepTarget={onSetItemSpecKeepTarget}
          onPickArrayItem={onPickArrayItem}
          onListArray={onListArray}
          onEmit={onEmit}
        />
      ))}
    </div>
  );
}

interface ArrayModeToggleProps {
  node: CatalogTreeNode;
  isTarget: boolean;
  hasItemSpec: boolean;
  /** true면 둘 다 미선택 + "— 혼합 —" 라벨(이 배열의 항목 모드가 셀마다 다름). */
  modeMixed: boolean;
  modeName: string;
  onListArray: (key: string) => void;
  onPickArrayItem: (key: string) => void;
}

/**
 * 배열 항목 모드 토글: [전체 나열 / 특정 항목]. itemSpec 유무로 모드를 판정한다.
 *   - '전체 나열' = 배열이 target + itemSpec 없음(repeat). 선택 시 itemSpec 제거 + target.
 *   - '특정 항목' = itemSpec 존재(인덱스/조건). 선택 시 index 0 지정.
 * 배열이 현재 바인딩 경로에 없으면(off-path) 둘 다 미선택.
 * 모드 혼합(modeMixed)이면 둘 다 미선택 + "— 혼합 —"로 표기(라디오 클릭 시 전체 통일).
 */
function ArrayModeToggle({
  node,
  isTarget,
  hasItemSpec,
  modeMixed,
  modeName,
  onListArray,
  onPickArrayItem,
}: ArrayModeToggleProps) {
  const listChecked = !modeMixed && isTarget && !hasItemSpec;
  const itemChecked = !modeMixed && hasItemSpec;
  return (
    <span className="inline-flex flex-wrap items-center gap-3 text-[10px]">
      <label className="inline-flex items-center gap-1 cursor-pointer">
        <input
          type="radio"
          name={modeName}
          checked={listChecked}
          onChange={() => onListArray(node.key)}
          className="shrink-0"
        />
        <span className={listChecked ? 'font-semibold text-brand-700' : 'text-gray-500'}>
          전체 나열(repeat)
        </span>
      </label>
      <label className="inline-flex items-center gap-1 cursor-pointer">
        <input
          type="radio"
          name={modeName}
          checked={itemChecked}
          onChange={() => {
            if (!hasItemSpec) onPickArrayItem(node.key);
          }}
          className="shrink-0"
        />
        <span className={itemChecked ? 'font-semibold text-brand-700' : 'text-gray-500'}>
          특정 항목
        </span>
      </label>
      {modeMixed && <span className="text-gray-400">— 혼합 —</span>}
    </span>
  );
}

interface ArrayItemControlsProps {
  node: CatalogTreeNode;
  spec: ArrayItemSpec;
  /** 일괄 혼합: 인덱스 값이 셀마다 다름 → 입력란을 '-' placeholder(빈 값)로(입력 시 전체 통일). */
  indexMixed?: boolean;
  /** 일괄 혼합: 조건 값이 셀마다 다름 → "조건 — 혼합 —"로 표시. */
  condMixed?: boolean;
  onSetItemSpec: (key: string, spec: ArrayItemSpec) => void;
}

/**
 * 배열 노드 항목 지정: 인덱스 입력만. 조건(필터)은 직속 leaf 필드 옆 인라인 필터로
 * 설정하며, 여기서는 필터가 활성일 때 회색 readonly 힌트("조건: 필드=값")로만 보여준다.
 * 일괄 혼합(indexMixed/condMixed)이면 그 값만 '-'/"— 혼합 —"로 나타낸다.
 */
function ArrayItemControls({
  node,
  spec,
  indexMixed = false,
  condMixed = false,
  onSetItemSpec,
}: ArrayItemControlsProps) {
  if (spec.mode === 'condition') {
    const field = node.children.find((c) => c.segName === spec.condKey);
    const fieldLabel = field?.label ?? spec.condKey;
    const valueSet = field?.valueSetId ? VALUE_SETS[field.valueSetId] : undefined;
    const valLabel = valueSet?.find((o) => o.value === spec.condVal)?.label ?? spec.condVal;
    return (
      <span className="flex flex-wrap items-center gap-1 text-[10px] text-gray-500">
        <span className="text-gray-400">항목:</span>
        <span className="text-gray-500">
          {condMixed ? '조건 — 혼합 —' : `조건 ${fieldLabel}=${valLabel || '(미지정)'}`}
        </span>
      </span>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-1 text-[10px] text-gray-500">
      <span className="text-gray-400">항목:</span>
      <span className="text-gray-400">인덱스</span>
      <input
        type="number"
        min={0}
        value={indexMixed ? '' : spec.index}
        placeholder={indexMixed ? '-' : undefined}
        onChange={(e) => {
          const n = Number.parseInt(e.target.value, 10);
          onSetItemSpec(node.key, { mode: 'index', index: Number.isNaN(n) || n < 0 ? 0 : n });
        }}
        className={`${FIELD_CLS} w-14 ${indexMixed ? 'bg-gray-50 text-gray-400' : ''}`}
      />
    </span>
  );
}

interface MapArrayControlsProps {
  node: CatalogTreeNode;
  info: MapArrayInfo;
  state: TreeState;
  /** true면 특정 항목/전체 나열 둘 다 미선택 + "— 혼합 —"(키 선택 시 전체 통일). */
  mixed: boolean;
  radioName: string;
  onEmit: (next: TreeState) => void;
}

/**
 * 키-값 맵 배열의 맵 뷰 컨트롤. "특정 항목"(키 드롭다운 → 값 출력) 또는
 * "전체 나열(repeat)" 중 하나를 선택한다. 키 선택 시 부모 배열 itemSpec을
 * condition(condKey=keyField.segName)으로, target을 값 필드로 동시 설정한다.
 * 값 필드가 여러 개면 값 필드 드롭다운, 1개면 라벨로 표시한다.
 * 엔진 select는 기존과 동일(`array[key=X].value`).
 */
function MapArrayControls({ node, info, state, mixed, radioName, onEmit }: MapArrayControlsProps) {
  const { keyField, valueFields } = info;
  const valueSet = keyField.valueSetId ? VALUE_SETS[keyField.valueSetId] : undefined;

  const spec = state.itemSpecs[node.key];
  const targetIsValueField = valueFields.some((v) => v.key === state.targetKey);
  // 혼합이면 어떤 모드도 선택 표시하지 않는다.
  const condActive =
    !mixed &&
    spec?.mode === 'condition' &&
    spec.condKey === keyField.segName &&
    targetIsValueField;
  const condVal = condActive && spec?.mode === 'condition' ? spec.condVal : '';
  // 현재 출력 값 필드(미선택 시 첫 값 필드).
  const selectedValueField = valueFields.find((v) => v.key === state.targetKey) ?? valueFields[0];
  const listActive = !mixed && state.targetKey === node.key;

  // 키 값 + 값 필드를 함께 적용(특정 항목 활성화).
  const applyItem = (condValNext: string, valueFieldKey: string) =>
    onEmit({
      targetKey: valueFieldKey,
      itemSpecs: {
        ...state.itemSpecs,
        [node.key]: { mode: 'condition', condKey: keyField.segName, condVal: condValNext },
      },
    });

  return (
    <div className="flex flex-col gap-1 text-[11px]">
      {mixed && <span className="text-[10px] text-gray-400">— 혼합 —</span>}
      {/* 특정 항목: 키 선택 → 값 출력 */}
      <div className="flex flex-wrap items-center gap-1">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="radio"
            name={radioName}
            checked={condActive}
            onChange={() => applyItem(condVal, selectedValueField.key)}
            className="shrink-0"
          />
          <span className={condActive ? 'font-semibold text-brand-700' : 'text-gray-600'}>
            특정 항목
          </span>
        </label>
        {valueSet ? (
          <select
            value={condVal}
            disabled={!condActive}
            onChange={(e) => applyItem(e.target.value, selectedValueField.key)}
            className={`${FIELD_CLS} w-28`}
          >
            <option value="">{keyField.label} 선택…</option>
            {valueSet.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={condVal}
            disabled={!condActive}
            placeholder={keyField.label}
            onChange={(e) => applyItem(e.target.value, selectedValueField.key)}
            className={`${FIELD_CLS} w-20`}
          />
        )}
        {/* '→ 값필드'를 한 단위로 묶어 '인원 수'만 단독 줄바꿈되는 것을 방지. */}
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <span className="text-gray-400">→</span>
          {valueFields.length > 1 ? (
            <select
              value={selectedValueField.key}
              disabled={!condActive}
              onChange={(e) => onEmit({ targetKey: e.target.value, itemSpecs: state.itemSpecs })}
              className={`${FIELD_CLS} w-24`}
            >
              {valueFields.map((vf) => (
                <option key={vf.key} value={vf.key}>
                  {vf.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-gray-600">{valueFields[0].label}</span>
          )}
        </span>
      </div>

      {/* 전체 나열(repeat) */}
      <div className="flex items-center gap-1">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="radio"
            name={radioName}
            checked={listActive}
            onChange={() => onEmit({ targetKey: node.key, itemSpecs: state.itemSpecs })}
            className="shrink-0"
          />
          <span className={listActive ? 'font-semibold text-brand-700' : 'text-gray-600'}>
            전체 나열(repeat)
          </span>
        </label>
      </div>
    </div>
  );
}

interface LeafFilterProps {
  arrayKey: string;
  leaf: CatalogTreeNode;
  spec: ArrayItemSpec | undefined;
  onSetItemSpec: (key: string, spec: ArrayItemSpec) => void;
}

/**
 * 배열 직속 leaf 필드 옆 인라인 필터(= 값). 체크 시 부모 배열 itemSpec을
 * condition(condKey=leaf.segName)으로 설정, 해제/값 비우기 시 index 0 복귀.
 * enum 필드는 VALUE_SETS 라벨 드롭다운, 아니면 텍스트. 배열당 필터 1개(조건 단일).
 */
function LeafFilter({ arrayKey, leaf, spec, onSetItemSpec }: LeafFilterProps) {
  const active = spec?.mode === 'condition' && spec.condKey === leaf.segName;
  const condVal = active && spec?.mode === 'condition' ? spec.condVal : '';
  const valueSet = leaf.valueSetId ? VALUE_SETS[leaf.valueSetId] : undefined;

  const toggle = (on: boolean) =>
    onSetItemSpec(
      arrayKey,
      on
        ? { mode: 'condition', condKey: leaf.segName, condVal: '' }
        : { mode: 'index', index: 0 },
    );
  // 값 비우기 → index 기본 복귀(필터 해제), 값 설정 → condition.
  const setVal = (v: string) =>
    onSetItemSpec(
      arrayKey,
      v === ''
        ? { mode: 'index', index: 0 }
        : { mode: 'condition', condKey: leaf.segName, condVal: v },
    );

  return (
    <span className="inline-flex flex-wrap items-center gap-0.5 text-[10px] text-gray-500">
      <label className="inline-flex items-center gap-0.5 cursor-pointer">
        <input type="checkbox" checked={active} onChange={(e) => toggle(e.target.checked)} />
        필터
      </label>
      {active && (
        <>
          <span className="text-gray-400">=</span>
          {valueSet ? (
            <select
              value={condVal}
              onChange={(e) => setVal(e.target.value)}
              className={`${FIELD_CLS} w-24`}
            >
              <option value="">선택…</option>
              {valueSet.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={condVal}
              placeholder="값"
              onChange={(e) => setVal(e.target.value)}
              className={`${FIELD_CLS} w-16`}
            />
          )}
        </>
      )}
    </span>
  );
}
